import { compareSortKeys, normalizeSlug, parseSortKey } from "./slug";
import { parseArticle } from "./markdown";
import type { ArchiveNode, Article, ContentStore, MarkdownFile, Series, SeriesChapter, Summary } from "./types";

interface SeriesItem {
  article: Article;
  order: ReturnType<typeof parseSortKey>;
  chapterSlug: string | null;
}

export function buildContentStore(files: MarkdownFile[], contentPath: string): ContentStore {
  const root = contentPath.replace(/^\/+|\/+$/g, "");
  const articles = new Map<string, Article>();
  const summaries: Summary[] = [];
  const seriesMap = new Map<string, Series>();
  const grouped = groupFiles(files, root);

  for (const file of grouped.standalone) {
    const article = parseArticle(file.path, file.body);
    addArticle(articles, summaries, article);
  }

  for (const [seriesTitle, seriesFiles] of grouped.series) {
    const { series, articles: seriesArticles } = buildSeries(seriesTitle, seriesFiles);
    if (series.article_count === 0) {
      continue;
    }
    if (seriesMap.has(series.slug)) {
      throw new Error(`duplicate series slug "${series.slug}"`);
    }
    seriesMap.set(series.slug, series);
    for (const article of seriesArticles) {
      addArticle(articles, summaries, article);
    }
  }

  summaries.sort((left, right) => Date.parse(right.published_at) - Date.parse(left.published_at));
  const seriesList = Array.from(seriesMap.values()).sort((left, right) => left.title.localeCompare(right.title));
  return {
    articles,
    summaries,
    series: seriesMap,
    seriesList,
    archiveTree: buildArchiveTree(summaries, seriesList),
  };
}

function groupFiles(files: MarkdownFile[], root: string) {
  const standalone: MarkdownFile[] = [];
  const series = new Map<string, MarkdownFile[]>();

  for (const file of files) {
    const relative = file.path.replace(new RegExp(`^${escapeRegExp(root)}/?`), "");
    const segments = relative.split("/");
    if (segments.length === 1) {
      standalone.push(file);
      continue;
    }
    const seriesTitle = segments[0];
    if (segments.length > 3) {
      throw new Error(`nested directory "${relative}" is invalid`);
    }
    const group = series.get(seriesTitle) || [];
    group.push(file);
    series.set(seriesTitle, group);
  }

  return { standalone, series };
}

function buildSeries(seriesTitle: string, files: MarkdownFile[]): { series: Series; articles: Article[] } {
  const seriesSlug = normalizeSlug(seriesTitle);
  if (!seriesSlug) {
    throw new Error("series slug is required");
  }

  const rootItems: SeriesItem[] = [];
  const chapterItems = new Map<string, SeriesItem[]>();
  const chapterTitles = new Map<string, string>();

  for (const file of files) {
    const segments = file.path.split("/");
    const filename = segments[segments.length - 1];
    const seriesIndex = segments.findIndex((segment) => segment === seriesTitle);
    const relativeSegments = segments.slice(seriesIndex + 1);
    const article = parseArticle(file.path, file.body);

    if (relativeSegments.length === 1) {
      rootItems.push({ article, order: parseSortKey(filename), chapterSlug: null });
      continue;
    }

    const chapterTitle = relativeSegments[0];
    const chapterSlug = normalizeSlug(chapterTitle);
    if (!chapterSlug) {
      throw new Error(`chapter "${chapterTitle}" slug is required`);
    }
    chapterTitles.set(chapterSlug, chapterTitle);
    const items = chapterItems.get(chapterSlug) || [];
    items.push({ article, order: parseSortKey(filename), chapterSlug });
    chapterItems.set(chapterSlug, items);
  }

  rootItems.sort((left, right) => compareSortKeys(left.order, right.order));
  const chapterOrder = Array.from(chapterItems.keys()).sort((left, right) =>
    (chapterTitles.get(left) || "").localeCompare(chapterTitles.get(right) || "")
  );

  const series: Series = {
    slug: seriesSlug,
    title: seriesTitle,
    article_count: 0,
    root_entries: [],
    chapters: [],
    all_entries: [],
  };
  const allArticles: Article[] = [];

  for (const [index, item] of rootItems.entries()) {
    const position = index + 1;
    const article = withSeriesMetadata(item.article, seriesSlug, seriesTitle, position, null, null, null);
    const summary = toSummary(article);
    series.root_entries.push(summary);
    series.all_entries.push(summary);
    allArticles.push(article);
  }

  for (const [chapterIndex, chapterSlug] of chapterOrder.entries()) {
    const items = chapterItems.get(chapterSlug) || [];
    items.sort((left, right) => compareSortKeys(left.order, right.order));
    const chapterPosition = chapterIndex + 1;
    const chapterTitle = chapterTitles.get(chapterSlug) || chapterSlug;
    const chapter: SeriesChapter = {
      slug: chapterSlug,
      title: chapterTitle,
      position: chapterPosition,
      entries: [],
    };
    for (const item of items) {
      const position = series.all_entries.length + 1;
      const article = withSeriesMetadata(item.article, seriesSlug, seriesTitle, position, chapterSlug, chapterTitle, chapterPosition);
      const summary = toSummary(article);
      chapter.entries.push(summary);
      series.all_entries.push(summary);
      allArticles.push(article);
    }
    series.chapters.push(chapter);
  }

  const summariesBySlug = new Map(series.all_entries.map((entry) => [entry.slug, entry]));
  allArticles.forEach((article, index) => {
    article.prev_in_series = index > 0 ? { ...summariesBySlug.get(series.all_entries[index - 1].slug)! } : null;
    article.next_in_series = index + 1 < allArticles.length ? { ...summariesBySlug.get(series.all_entries[index + 1].slug)! } : null;
  });

  series.article_count = series.all_entries.length;
  return { series, articles: allArticles };
}

function addArticle(articles: Map<string, Article>, summaries: Summary[], article: Article): void {
  if (articles.has(article.slug)) {
    throw new Error(`duplicate slug "${article.slug}"`);
  }
  articles.set(article.slug, article);
  summaries.push(toSummary(article));
}

export function toSummary(article: Article): Summary {
  const { body, html, prev_in_series, next_in_series, ...summary } = article;
  void body;
  void html;
  void prev_in_series;
  void next_in_series;
  return { ...summary, tags: [...summary.tags] };
}

function withSeriesMetadata(
  article: Article,
  seriesSlug: string,
  seriesTitle: string,
  seriesPosition: number,
  chapterSlug: string | null,
  chapterTitle: string | null,
  chapterPosition: number | null
): Article {
  return {
    ...article,
    series_slug: seriesSlug,
    series_title: seriesTitle,
    series_position: seriesPosition,
    chapter_slug: chapterSlug,
    chapter_title: chapterTitle,
    chapter_position: chapterPosition,
  };
}

function buildArchiveTree(summaries: Summary[], seriesList: Series[]): ArchiveNode[] {
  const nodes: ArchiveNode[] = [];

  for (const summary of summaries) {
    if (summary.series_slug) {
      continue;
    }
    nodes.push({ kind: "article", title: summary.title, slug: summary.slug, summary: { ...summary } });
  }

  for (const series of seriesList) {
    const children: ArchiveNode[] = [];
    for (const entry of series.root_entries) {
      children.push({ kind: "article", title: entry.title, slug: entry.slug, summary: { ...entry } });
    }
    for (const chapter of series.chapters) {
      children.push({
        kind: "group",
        title: chapter.title,
        slug: chapter.slug,
        children: chapter.entries.map((entry) => ({ kind: "article", title: entry.title, slug: entry.slug, summary: { ...entry } })),
      });
    }
    nodes.push({
      kind: "group",
      title: series.title,
      slug: series.slug,
      link: `/series/${series.slug}`,
      children,
    });
  }

  return nodes.sort((left, right) => {
    if (left.kind !== right.kind) {
      return left.kind === "group" ? -1 : 1;
    }
    return left.title.localeCompare(right.title);
  });
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
